import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Subject, Subscription } from "rxjs";

import { ISubscription, IBillingData, IDependents } from "@edp-models/edp-pack-subscriber.model";
import { Contract } from "@edp-components/edp-contract/edp-contract.model";
import { Pack } from "@edp-components/edp-pack/edp-pack.model";
import { environment } from "@edp-environments/environment";
import { ContractService } from "@edp-services/contract.service";
import { PackService } from "@edp-services/pack.service";
import { EdpResponse } from "@edp-models/response.model";
import { Discount } from "@edp-components/edp-pack-discount";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { IAppState } from "@edp-store/state/app.state";
import { Store } from "@ngrx/store";
import { SetPack } from "@edp-store/actions/contract.actions";
import { getPack } from "@edp-store/selectors/contract.selectors";

@Injectable()
export class PackSubscriberService {
    public submitIntention$: Subject<boolean> = new Subject<boolean>();
    public isReadyToSubmit$: Subject<boolean> = new Subject<boolean>();
    public submitStatus$: Subject<boolean> = new Subject<boolean>();
    public doTheSubmit$: Subject<boolean> = new Subject<boolean>();
    public billingData$: Subject<IBillingData | boolean> = new Subject<IBillingData | boolean>();
    public dependents$: Subject<IDependents[] | boolean> = new Subject<IDependents[] | boolean>();
    public resumePage$: Subject<boolean> = new Subject<boolean>();
    public loading$: Subject<boolean> = new Subject<boolean>();
    public submittedWithPO = false;

    private resumePage: boolean;
    private contaCerta: number;
    private contract: Contract;
    private pack: Pack;
    private billingData: IBillingData;
    private dependents: IDependents[];
    private billingSubscription: Subscription;
    private dependentsSubscription: Subscription;
    private submittedIntention = false;
    private service: boolean;

    constructor(
        private http: HttpClient,
        private contractService: ContractService,
        private packService: PackService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private store: Store<IAppState>
    ) {
        this.restoreSession();
        this.billingSubscribe();
        this.dependentsSubscribe();

        this.packService.contacertaChanged$.subscribe(() => {
            this.dependentsSubscription.unsubscribe();
            if (this.pack.hasHealthPlan) {
                this.dependentsSubscribe();
            }
        });

        this.doTheSubmit$.subscribe(submit => {
            if (submit) {
                if (window.innerWidth <= SCREEN_SIZES.MD && !this.resumePage) {
                    this.setResumePage();
                } else {
                    this.submit(this.billingData, this.dependents);
                }
            }
        });
    }

    public billingSubscribe() {
        this.billingSubscription = this.billingData$.subscribe(billingData => {
            this.billingData = billingData as IBillingData;
            const dependents = this.dependents as IDependents[] | boolean;
            if (!this.pack.hasHealthPlan && this.submittedIntention) {
                this.isReadyToSubmit$.next(billingData !== false);
            } else {
                this.isReadyToSubmit$.next(billingData !== false && dependents !== false && this.submittedIntention);
            }
        });
    }

    public dependentsSubscribe() {
        this.dependentsSubscription = this.dependents$.subscribe(dependents => {
            this.dependents = dependents as IDependents[];
        });
    }

    public dispatchSubmitIntention(submited: boolean): void {
        this.submittedIntention = submited;
        this.submitIntention$.next(submited);
    }

    public setBillingData(billingData: IBillingData | boolean): void {
        this.billingData$.next(billingData);
    }

    public setDependents(dependents: IDependents[] | boolean): void {
        this.dependents$.next(dependents);
    }

    public getDependents(): IDependents[] {
        return this.dependents;
    }

    public setResumePage(resume: boolean = true): void {
        this.resumePage = resume;
        this.resumePage$.next(this.resumePage);
    }

    public setContract(contract: Contract): void {
        sessionStorage.setItem("selected-contract", JSON.stringify(contract));
        this.contract = contract;
    }

    public clearContract(): void {
        sessionStorage.removeItem("selected-contract");
        this.contract = null;
    }

    public setPack(pack: Pack): void {
        sessionStorage.setItem("selected-pack", JSON.stringify(pack));
        this.pack = pack;
    }

    public getContract(): Contract {
        return this.contract;
    }

    public getPack(): Pack {
        let newPack = null;
        this.store.select(getPack).subscribe((pack: Pack) => {
            newPack = !!pack ? pack : this.pack;
        });

        return newPack;
    }

    public setContaCerta(value: number): void {
        this.contaCerta = value;
    }

    public getContaCerta(): number {
        return this.contaCerta;
    }

    public submitSubscriptionRequest(body: ISubscription) {
        this.loading$.next(true);

        this.http.post(`${environment.apiUrl}pack.subscribe`, { args: body }).subscribe(
            (response: EdpResponse) => {
                this.submittedWithPO = response.Body.Result.Collection.PO;
                this.submitStatus$.next(response.Body.Success);
                this.loading$.next(false);
                this.googleAnalyticsService.datalayerEvent("SUBSCRIBE_PACK");
            },
            () => {
                this.submitStatus$.next(false);
                this.loading$.next(false);
            }
        );
    }

    public submit(billingData: IBillingData, dependents: IDependents[] = []) {
        const discount: Discount = this.packService.getDiscount();
        const totalPackValue: string = this.packService.getTotalPackValue();
        const body: ISubscription = {
            rule: "packs",
            pack_id: this.pack.id,
            contract_id: this.contract.ContractId,
            iban: billingData.iban,
            email: billingData.email,
            conta_certa: this.contaCerta + "",
            discount_E: discount.values.electricity,
            discount_G: discount.values.gas,
            pack_value: totalPackValue,
            dependents,
            hasImmediateSupply: this.service,
        };
        this.submitSubscriptionRequest(body);
    }

    public restoreSession(): void {
        const storagePack = JSON.parse(sessionStorage.getItem("selected-pack"));
        const storageContract = JSON.parse(sessionStorage.getItem("selected-contract"));

        if (storagePack !== null) {
            const pack: Pack = this.packService.createPack(storagePack);
            this.setPack(pack);
        }

        if (storageContract !== null) {
            const contract: Contract = this.contractService.createContract(storageContract);
            this.setContract(contract);
        }
    }

    public setService(service) {
        this.service = service;
    }
}
